import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { getPortalProfitability } from "@/lib/portal-sla.functions";
import { PortalPageHeader } from "@/components/portal-shell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export const Route = createFileRoute("/_authenticated/portal/profitability")({ component: ProfitabilityPage });

function fmtMoney(c: number | null | undefined) {
  if (c == null) return "—";
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(Number(c) / 100);
}

function ProfitabilityPage() {
  const fetchFn = useServerFn(getPortalProfitability);
  const [months, setMonths] = useState("3");
  const { data, isLoading } = useQuery({
    queryKey: ["portal-profitability", months],
    queryFn: () => fetchFn({ data: { months: parseInt(months, 10) } }),
  });
  const rows = data?.rows ?? [];
  const latest = rows[0];

  return (
    <>
      <PortalPageHeader
        title="Cost & Profitability"
        description="What it costs us to ship for you each month, with revenue and margin."
      />
      <div className="p-4 sm:p-6 space-y-6">
        <div className="flex justify-end">
          <Select value={months} onValueChange={setMonths}>
            <SelectTrigger className="w-36 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last month</SelectItem>
              <SelectItem value="3">Last 3 months</SelectItem>
              <SelectItem value="6">Last 6 months</SelectItem>
              <SelectItem value="12">Last 12 months</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Latest Revenue</CardTitle></CardHeader>
            <CardContent><div className="text-3xl font-semibold tabular-nums">{fmtMoney(latest?.revenue_cents)}</div></CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Latest Cost</CardTitle></CardHeader>
            <CardContent><div className="text-3xl font-semibold tabular-nums">{fmtMoney(latest?.total_cost_cents)}</div></CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Margin</CardTitle></CardHeader>
            <CardContent>
              <div className="text-3xl font-semibold tabular-nums">{fmtMoney(latest?.margin_cents)}</div>
              <div className="text-xs text-muted-foreground mt-1">{latest?.margin_pct != null ? `${Number(latest.margin_pct).toFixed(1)}%` : "—"}</div>
            </CardContent>
          </Card>
        </div>

        <div className="border rounded-lg bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Month</TableHead>
              <TableHead className="text-right">Revenue</TableHead>
              <TableHead className="text-right">Labels</TableHead>
              <TableHead className="text-right">Consumables</TableHead>
              <TableHead className="text-right">Labor</TableHead>
              <TableHead className="text-right">Total cost</TableHead>
              <TableHead className="text-right">Margin</TableHead>
              <TableHead className="text-right">Margin %</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
              ) : rows.length > 0 ? rows.map((r: any) => (
                <TableRow key={`${r.period_month}-${r.customer_id}`}>
                  <TableCell className="font-medium">{new Date(r.period_month).toLocaleDateString(undefined, { year: "numeric", month: "short" })}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtMoney(r.revenue_cents)}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{fmtMoney(r.label_cost_cents)}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{fmtMoney(r.consumable_cost_cents)}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{fmtMoney(r.labor_cost_cents)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtMoney(r.total_cost_cents)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtMoney(r.margin_cents)}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.margin_pct != null ? `${Number(r.margin_pct).toFixed(1)}%` : "—"}</TableCell>
                </TableRow>
              )) : (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No cost data yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  );
}